import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function Login() {
  const [step, setStep] = useState('email'); // 'email' | 'password' | 'local' | 'sso-redirect'
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ssoInfo, setSsoInfo] = useState(null);
  const { login } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for SSO error from redirect
  useEffect(() => {
    const ssoError = searchParams.get('sso_error');
    if (ssoError) setError(ssoError);
  }, [searchParams]);

  // Step 1: Email lookup
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/sso/lookup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      if (data.type === 'sso') {
        setSsoInfo(data);
        setStep('sso-redirect');
        setTimeout(() => {
          window.location.href = `/api/sso/authorize/${data.configId}`;
        }, 500);
      } else {
        setStep('password');
      }
    } catch (err) {
      setError(err.message || 'Failed to check email');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Password login (email-based)
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2-alt: Local username+password login
  const handleLocalSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('email');
    setPassword('');
    setUsername('');
    setError('');
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: 8, border: '1px solid var(--border)',
    background: 'var(--bg-input)', color: 'var(--text)', fontSize: 14, outline: 'none'
  };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' };
  const buttonStyle = {
    width: '100%', padding: '12px 20px', borderRadius: 8, border: 'none',
    background: 'var(--primary)', color: 'white', fontSize: 15, fontWeight: 600,
    cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.7 : 1, transition: 'all 0.2s'
  };
  const backBtnStyle = {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
    color: 'var(--text-secondary)', padding: '0 4px', lineHeight: 1
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: theme === 'dark' ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      padding: 20
    }}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
        padding: 40, width: '100%', maxWidth: 440, border: '1px solid var(--border)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src="/logo.svg" alt="AttackPath" style={{ width: 72, height: 72, marginBottom: 8 }} />
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>AttackPath</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Sign in to continue</p>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px', borderRadius: 8, background: '#fef2f2', color: '#dc2626',
            border: '1px solid #fecaca', marginBottom: 16, fontSize: 14
          }}>{error}</div>
        )}

        {/* Step 1: Email input */}
        {step === 'email' && (
          <>
            <form onSubmit={handleEmailSubmit}>
              <div style={{ marginBottom: 24 }}>
                <label style={labelStyle}>Email Address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder="Enter your email address" autoFocus style={inputStyle} />
              </div>
              <button type="submit" disabled={loading} style={buttonStyle}>
                {loading ? 'Checking...' : 'Continue'}
              </button>
            </form>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0',
              color: 'var(--text-secondary)', fontSize: 13
            }}>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span>or</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>

            <button onClick={() => { setStep('local'); setError(''); }}
              style={{
                width: '100%', padding: '11px 20px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--bg-input)',
                color: 'var(--text)', fontSize: 14, fontWeight: 500,
                cursor: 'pointer', transition: 'all 0.2s'
              }}>
              Sign in with password
            </button>
          </>
        )}

        {/* Step 2: Password (after email lookup) */}
        {step === 'password' && (
          <form onSubmit={handlePasswordSubmit}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <button type="button" onClick={handleBack} style={backBtnStyle}>&#8592;</button>
              <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{email}</span>
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="Enter your password" autoFocus style={inputStyle} />
            </div>
            <button type="submit" disabled={loading} style={buttonStyle}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {/* Step 2-alt: Local username + password */}
        {step === 'local' && (
          <form onSubmit={handleLocalSubmit}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <button type="button" onClick={handleBack} style={backBtnStyle}>&#8592;</button>
              <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Sign in with password</span>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Username</label>
              <input type="text" value={username} onChange={e => setUsername(e.target.value)} required
                placeholder="Enter your username" autoFocus style={inputStyle} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                required placeholder="Enter your password" style={inputStyle} />
            </div>
            <button type="submit" disabled={loading} style={buttonStyle}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        {/* Step 3: SSO redirect in progress */}
        {step === 'sso-redirect' && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔐</div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>
              Redirecting to {ssoInfo?.providerName}...
            </div>
            {ssoInfo?.clientName && (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>({ssoInfo.clientName})</div>
            )}
          </div>
        )}

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <button onClick={toggle} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 13
          }}>
            {theme === 'dark' ? '☀️ Light Mode' : '🌙 Dark Mode'}
          </button>
        </div>

      </div>
    </div>
  );
}
